/**
 * Power-availability report section fallbacks (SNT-5).
 *
 * Pure deterministic fallbacks for the free-text fields on the
 * `power-availability` report-type schema (siteRag.ts:1679-1718). Each
 * composer surfaces data from `sed.grid`, `sed.electricity`,
 * `sed.solar`, `sed.wind` — no AI calls, no I/O, no randomness.
 *
 * Contract & composition: same as `feasibility.ts` / `environmental.ts`.
 * The recursion guard R6 is pinned by
 * `instructionShapeGuard.test.ts -> "fallback recursion guard"`.
 */
import type { SiteEntity } from '../../types/site';
import type { SiteEvidenceDocument } from '../../types/siteEvidence';
import { formatElectricityPriceReference } from '../reportFormatters';

/* ───────────────────────── helpers ──────────────────────────── */

/** Format a numeric km distance with one decimal, or `[—]` when missing. */
function kmLabel(value: number | null | undefined): string {
  if (typeof value !== 'number' || !Number.isFinite(value)) return '[—]';
  return `${value.toFixed(1)} km`;
}

/**
 * Compact "substation + voltage" descriptor sourced from `sed.grid.data`.
 * Falls back to `[—]` when the slot is unavailable.
 */
function substationDescriptor(sed: SiteEvidenceDocument): string {
  const gd = sed.grid?.data;
  if (!gd) return '[—]';
  const name = typeof gd.substationName === 'string' && gd.substationName.length > 0 ? gd.substationName : 'closest substation';
  const dist = kmLabel(gd.substationDistKm);
  const voltage = typeof gd.voltage === 'string' && gd.voltage.length > 0 ? gd.voltage : '[—]';
  return `${name} at ${dist}, ${voltage}`;
}

/* ───────────────────────── composers ────────────────────────── */

/**
 * Executive summary fallback for power-availability reports.
 */
export function powerExecutiveSummaryFallback(
  site: SiteEntity,
  sed: SiteEvidenceDocument,
): string {
  const parts: string[] = [];
  parts.push(`${site.name} power-availability summary.`);
  const gd = sed.grid?.data;
  if (gd) {
    const sub: string[] = [];
    sub.push(`substation ${kmLabel(gd.substationDistKm)}`);
    if (gd.voltage) sub.push(`voltage ${gd.voltage}`);
    if (gd.capacity) sub.push(`capacity ${gd.capacity}`);
    if (gd.isoRegion) sub.push(`ISO ${gd.isoRegion}`);
    parts.push(`Grid baseline: ${sub.join('; ')}.`);
  }
  const elec = sed.electricity?.data;
  if (elec && typeof elec.retailRate === 'number' && Number.isFinite(elec.retailRate)) {
    parts.push(formatElectricityPriceReference(elec));
  }
  parts.push('Detailed power narrative pending re-run.');
  return parts.join(' ');
}

/**
 * Substation assessment fallback. Pulls distance + voltage + utility
 * + transmission proximity.
 */
export function substationAssessmentFallback(
  _site: SiteEntity,
  sed: SiteEvidenceDocument,
): string {
  const gd = sed.grid?.data;
  if (!gd) return 'Substation assessment pending re-run.';
  const parts: string[] = [];
  parts.push(`Closest substation: ${substationDescriptor(sed)}.`);
  if (gd.utilityName) parts.push(`Utility: ${gd.utilityName}.`);
  if (typeof gd.substationsWithin50km === 'number' && Number.isFinite(gd.substationsWithin50km)) {
    parts.push(`${gd.substationsWithin50km} substation(s) within 50 km.`);
  }
  if (typeof gd.nearestTransmissionKm === 'number' && Number.isFinite(gd.nearestTransmissionKm)) {
    const tv = gd.nearestTransmissionVoltage ?? '[—]';
    parts.push(`Nearest transmission line ${kmLabel(gd.nearestTransmissionKm)} (${tv}).`);
  }
  parts.push('Interconnection cost detail pending re-run.');
  return parts.join(' ');
}

/**
 * Interconnection queue fallback. Reads `estimatedQueueTime` + ISO
 * region from `sed.grid.data`.
 */
export function interconnectionQueueFallback(
  _site: SiteEntity,
  sed: SiteEvidenceDocument,
): string {
  const gd = sed.grid?.data;
  if (!gd) return 'Interconnection queue assessment pending re-run.';
  const parts: string[] = [];
  if (gd.isoRegion) parts.push(`ISO region: ${gd.isoRegion}`);
  if (gd.estimatedQueueTime) parts.push(`estimated queue duration ${gd.estimatedQueueTime}`);
  if (gd.estimatedInterconnectionCost) parts.push(`indicative interconnection cost ${gd.estimatedInterconnectionCost}`);
  if (parts.length === 0) return 'Interconnection queue assessment pending re-run.';
  return `Queue baseline: ${parts.join('; ')}. Cluster-study detail pending re-run.`;
}

/**
 * Capacity analysis fallback. Composes from grid capacity tier +
 * transmission corridor count.
 */
export function capacityAnalysisFallback(
  _site: SiteEntity,
  sed: SiteEvidenceDocument,
): string {
  const gd = sed.grid?.data;
  if (!gd) return 'Capacity analysis pending re-run.';
  const parts: string[] = [];
  if (gd.capacity) parts.push(`tier ${gd.capacity}`);
  if (gd.complexity) parts.push(`interconnect complexity ${gd.complexity}`);
  if (Array.isArray(gd.transmissionLines) && gd.transmissionLines.length > 0) {
    parts.push(`${gd.transmissionLines.length} transmission corridor(s) catalogued`);
  }
  if (parts.length === 0) return 'Capacity analysis pending re-run.';
  return `Capacity baseline: ${parts.join('; ')}. Headroom modelling pending re-run.`;
}

/**
 * Curtailment risk fallback. Reads renewable capacity factor + ISO
 * region as a proxy. The SED doesn't carry a direct curtailment
 * percentage today (the ITT pack flags it as a Wave-2 gap).
 */
export function curtailmentRiskFallback(
  _site: SiteEntity,
  sed: SiteEvidenceDocument,
): string {
  const wd = sed.wind?.data;
  const sd = sed.solar?.data;
  const gd = sed.grid?.data;
  const parts: string[] = [];
  if (wd && typeof wd.capacityFactor === 'number' && Number.isFinite(wd.capacityFactor)) {
    parts.push(`wind capacity factor ${(wd.capacityFactor * 100).toFixed(0)}%`);
  }
  if (sd && typeof sd.ghi === 'number' && Number.isFinite(sd.ghi)) {
    parts.push(`solar GHI ${sd.ghi.toFixed(1)} kWh/m^2/day`);
  }
  if (gd?.isoRegion) parts.push(`ISO ${gd.isoRegion}`);
  if (parts.length === 0) return 'Curtailment risk assessment pending re-run.';
  return `Renewable mix indicators: ${parts.join('; ')}. Site-specific curtailment factor pending re-run.`;
}

/**
 * Rate structure fallback. Preserves price context without inferring tariff terms.
 */
export function rateStructureFallback(
  _site: SiteEntity,
  sed: SiteEvidenceDocument,
): string {
  const e = sed.electricity?.data;
  const context = e ? formatElectricityPriceReference(e) : 'Electricity price reference unavailable.';
  return `${context} Applicable tariff class, demand charges, time-of-use and standby terms require utility-specific evidence; a state average cannot establish them.`;
}

/**
 * Renewable integration fallback. Combines solar + wind + electricity.
 */
export function renewableIntegrationFallback(
  _site: SiteEntity,
  sed: SiteEvidenceDocument,
): string {
  const parts: string[] = [];
  const sd = sed.solar?.data;
  if (sd) {
    if (typeof sd.ghi === 'number' && Number.isFinite(sd.ghi)) parts.push(`GHI ${sd.ghi.toFixed(1)} kWh/m^2/day`);
    if (typeof sd.dni === 'number' && Number.isFinite(sd.dni)) parts.push(`DNI ${sd.dni.toFixed(1)} kWh/m^2/day`);
  }
  const wd = sed.wind?.data;
  if (wd && typeof wd.windSpeed100m === 'number' && Number.isFinite(wd.windSpeed100m)) {
    parts.push(`wind ${wd.windSpeed100m.toFixed(1)} m/s at 100m`);
  }
  if (parts.length === 0) return 'Renewable integration narrative pending re-run.';
  return `Resource baseline: ${parts.join('; ')}. PPA structuring detail pending re-run.`;
}

/**
 * Redundancy assessment fallback. Reads substations-within-50km +
 * transmission corridor count as a proxy for redundancy.
 */
export function redundancyAssessmentFallback(
  _site: SiteEntity,
  sed: SiteEvidenceDocument,
): string {
  const gd = sed.grid?.data;
  if (!gd) return 'Redundancy assessment pending re-run.';
  const parts: string[] = [];
  if (typeof gd.substationsWithin50km === 'number' && Number.isFinite(gd.substationsWithin50km)) {
    parts.push(`${gd.substationsWithin50km} substation(s) within 50 km`);
  }
  if (Array.isArray(gd.transmissionLines) && gd.transmissionLines.length > 0) {
    parts.push(`${gd.transmissionLines.length} transmission corridor(s)`);
  }
  if (parts.length === 0) return 'Redundancy assessment pending re-run.';
  return `Redundancy indicators: ${parts.join('; ')}. N-1 contingency narrative pending re-run.`;
}

/**
 * Power-availability conclusion recommendation fallback.
 */
export function powerRecommendationFallback(
  _site: SiteEntity,
  _sed: SiteEvidenceDocument,
): string {
  return 'Power-availability recommendation pending re-run.';
}

const BTM_STRATEGY_SCOPE: Record<string, string> = {
  'grid-only': 'Grid-only posture — BTM technologies receive brief disqualification summaries only.',
  'btm-only': 'BTM-only posture — all in-scope self-generation technologies receive full deep-dive analysis; grid sections emphasize backup minimums.',
  hybrid: 'Hybrid posture — grid and 2–3 viable BTM technologies receive full comparative analysis.',
};

/**
 * Power strategy context — requirements summary from site name + grid baseline.
 */
export function powerStrategyRequirementsFallback(
  site: SiteEntity,
  sed: SiteEvidenceDocument,
): string {
  const gd = sed.grid?.data;
  const parts: string[] = [`${site.name} power procurement requirements.`];
  if (gd?.capacity) parts.push(`Grid capacity tier ${gd.capacity}`);
  if (gd?.isoRegion) parts.push(`ISO ${gd.isoRegion}`);
  parts.push('Detailed requirements narrative pending re-run.');
  return parts.join(' ');
}

export function powerStrategyPostureFallback(
  _site: SiteEntity,
  _sed: SiteEvidenceDocument,
): string {
  return 'Power strategy posture pending re-run — configure BTM strategy in Coverage Matrix report setup.';
}

export function powerStrategyScopeFallback(
  _site: SiteEntity,
  _sed: SiteEvidenceDocument,
): string {
  return 'Analysis scope pending re-run — BTM technology deep dives will reflect declared grid-only / hybrid / btm-only strategy.';
}

/** Build deterministic powerStrategyContext when LLM output is absent. */
export function buildPowerStrategyContextFallback(
  site: SiteEntity,
  sed: SiteEvidenceDocument,
  prefs?: { btmStrategy?: string },
): {
  requirementsSummary: string;
  strategyPosture: string;
  analysisScope: string;
  keyDecisionCriteria: string[];
} {
  const strategy = prefs?.btmStrategy;
  return {
    requirementsSummary: powerStrategyRequirementsFallback(site, sed),
    strategyPosture: strategy
      ? `${BTM_STRATEGY_SCOPE[strategy] ?? `Strategy: ${strategy}.`} Detail pending re-run.`
      : powerStrategyPostureFallback(site, sed),
    analysisScope: strategy
      ? BTM_STRATEGY_SCOPE[strategy] ?? powerStrategyScopeFallback(site, sed)
      : powerStrategyScopeFallback(site, sed),
    keyDecisionCriteria: [
      'Time-to-power vs interconnection queue duration',
      'All-in $/MWh — grid retail vs BTM LCOE',
      'Redundancy and uptime under declared BTM strategy',
    ],
  };
}
